import { useHistory } from "react-router-dom";
import { personWithBinoculars } from "swing-assets";
import { Button } from "swing-components";

import { translate } from "~utils";

export type NonSwingCertEmptyViewProps = {
  state?: "CA" | "NJ";
  hasSwingCert?: boolean;
  onboardingConfigurationId?: string;
  isSwingCertFeatureEnabled?: boolean;
};

/**
 * Component | NonSwingCertEmptyView
 *
 * Renders an empty state view when a user has no openings available and is
 * eligible for SwingCert. Displays information about SwingCert benefits and
 * provides a CTA to register.
 *
 * Conditionally routes to internal permit page or
 * external registration based on user state and configuration.
 */
export function NonSwingCertEmptyView(props: NonSwingCertEmptyViewProps) {
  const { state, hasSwingCert, onboardingConfigurationId, isSwingCertFeatureEnabled } = props;
  const history = useHistory();

  /*** Render #1 ***/
  if (state === undefined) return null;

  /*** Handlers ***/
  function handleClick() {
    // If user is in CA, doesn't have SwingCert, has the ca-ukg-swing-cert config,
    // AND the feature flag is enabled, redirect to the new /more/permit page
    if (
      state === "CA" &&
      hasSwingCert === false &&
      onboardingConfigurationId === "ca-ukg-swing-cert" &&
      isSwingCertFeatureEnabled === true
    ) {
      history.push("/more/permit");
    } else {
      // Otherwise, open external registration link
      const url = `https://start.swingeducation.com/swingcert-${state!.toLowerCase()}-register`;
      window.open(url, "_blank", "noreferrer");
    }
  }

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 24,
        padding: "0 16px",
      }}
    >
      <img src={personWithBinoculars} alt={translate("NON_SWING_CERT_EMPTY_VIEW_IMG_ALT_TEXT")} />
      <div css={{ textAlign: "center" }}>
        <span css={{ fontWeight: 600 }}>
          {translate("NON_SWING_CERT_EMPTY_VIEW_HEADER", { state })}
        </span>
        <p css={{ margin: 0, marginTop: 8 }}>
          {translate("NON_SWING_CERT_EMPTY_VIEW_SUB_HEADER", { state })}
        </p>
      </div>
      <Button onClick={handleClick}>
        {translate("NON_SWING_CERT_CTA", { multiplier: state === "CA" ? 2 : 3 })}
      </Button>
    </div>
  );
}
